import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Users, ShoppingCart, DollarSign, Truck, BellRing, Package, TrendingUp, Crown, Cake, Gift, Percent, Flame } from "lucide-react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import BundlesManager from "@/components/BundlesManager";
import ResidualPayoutSection from "@/components/ResidualPayoutSection";
import ClientLicenseAlert from "@/components/ClientLicenseAlert";
import TipEarningsCard from "@/components/TipEarningsCard";
import GoActiveToggle from "@/components/GoActiveToggle";
import PartnerPortalCard from "@/components/PartnerPortalCard";
import TrendingBanner from "@/components/TrendingBanner";
import EventModeration from "@/components/EventModeration";
import { isAdminUser } from "@/lib/roles";
import { Lock } from "lucide-react";

const Kpi = ({ icon: Icon, label, value, testid, compact = false, tone = "" }) => {
  const toneClass =
    tone === "amber" ? "border-amber-400 bg-amber-50" :
    tone === "emerald" ? "border-emerald-400 bg-emerald-50" : "";
  if (compact) {
    return (
      <Card className={`p-2 h-full ${toneClass ? "border-l-4 " + toneClass : ""}`} data-testid={testid}>
        <div className="flex items-center gap-1 text-gray-500">
          <Icon size={12} /> <span className="text-[9px] uppercase tracking-wider font-semibold truncate">{label}</span>
        </div>
        <div className="mt-1 text-lg font-bold tabular-nums truncate">{value}</div>
      </Card>
    );
  }
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-center gap-3 text-gray-500">
        <Icon size={18} /> <span className="label-eyebrow">{label}</span>
      </div>
      <div className="mt-3 text-3xl font-bold tracking-tight">{value}</div>
    </Card>
  );
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const canSeeHqSettings = isAdminUser(user); // admin + super_admin only
  const [stats, setStats] = useState({ clients: 0, orders: 0, revenue: 0, personnel: 0, lowStock: 0, alerts: [], outstandingPOs: 0, backOrders: 0 });
  const [trends, setTrends] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [vipClients, setVipClients] = useState([]);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("digest-dismissed") === new Date().toISOString().slice(0, 10));
  const [subDiscount, setSubDiscount] = useState("");
  const [discountLoaded, setDiscountLoaded] = useState(false);
  const [savingDiscount, setSavingDiscount] = useState(false);
  const [defaultSplit, setDefaultSplit] = useState("");
  const [splitLoaded, setSplitLoaded] = useState(false);
  const [savingSplit, setSavingSplit] = useState(false);
  const [trendingCount, setTrendingCount] = useState("");
  const [trendingCountLoaded, setTrendingCountLoaded] = useState(false);
  const [savingTrendingCount, setSavingTrendingCount] = useState(false);

  useEffect(() => {
    api.get("/settings/sub-store-discount")
      .then(r => setSubDiscount(Number(r.data?.percent) || 0))
      .catch(() => {})
      .finally(() => setDiscountLoaded(true));
    api.get("/settings/default-wholesale-split")
      .then(r => setDefaultSplit(Number(r.data?.percent) || 0))
      .catch(() => {})
      .finally(() => setSplitLoaded(true));
    api.get("/settings/trending-count")
      .then(r => setTrendingCount(Number(r.data?.top_n) || 3))
      .catch(() => {})
      .finally(() => setTrendingCountLoaded(true));
  }, []);

  const saveTrendingCount = async () => {
    const n = Math.max(1, Math.min(10, Math.round(Number(trendingCount) || 3)));
    setSavingTrendingCount(true);
    try {
      await api.put("/admin/settings/trending-count", { top_n: n });
      toast.success(`Trending shows top ${n}`);
      setTrendingCount(n);
    } catch { toast.error("Save failed"); }
    finally { setSavingTrendingCount(false); }
  };

  const saveDiscount = async () => {
    const pct = Math.max(0, Math.min(100, Number(subDiscount) || 0));
    setSavingDiscount(true);
    try {
      await api.put("/admin/settings/sub-store-discount", { percent: pct });
      toast.success(`Sub-store discount set to ${pct}%`);
      setSubDiscount(pct);
    } catch { toast.error("Save failed"); }
    finally { setSavingDiscount(false); }
  };

  const saveDefaultSplit = async () => {
    const pct = Math.max(0, Math.min(100, Number(defaultSplit) || 0));
    setSavingSplit(true);
    try {
      await api.put("/admin/settings/default-wholesale-split", { percent: pct });
      toast.success(`Default wholesale split set to ${pct}%`);
      setDefaultSplit(pct);
    } catch { toast.error("Save failed"); }
    finally { setSavingSplit(false); }
  };

  const dismissDigest = () => {
    const today = new Date().toISOString().slice(0, 10);
    localStorage.setItem("digest-dismissed", today);
    setDismissed(true);
  };

  useEffect(() => {
    (async () => {
      try {
        const [clients, orders, personnel, products, alerts, pos] = await Promise.all([
          api.get("/clients").then(r => r.data),
          api.get("/orders").then(r => r.data),
          api.get("/personnel").then(r => r.data).catch(() => []),
          api.get("/products").then(r => r.data),
          api.get("/alerts").then(r => r.data).catch(() => []),
          api.get("/purchase-orders").then(r => r.data).catch(() => []),
        ]);
        const revenue = orders.reduce((s, o) => s + (o.total || 0), 0);
        const pendingPayments = orders.filter(o => o.payment_status !== "paid").reduce((s, o) => s + (Number(o.total) || 0), 0);
        const dpRevenue = orders.filter(o => {
          const partner = String(o.fulfilled_by_name || o.assigned_to_name || o.distributing_partner_name || "").trim().toLowerCase();
          return partner && !["warehouse", "warehouse hq", "hq", "david anderson", "david anderson, jr.", "david anderson jr."].includes(partner) && (o.assigned_to_role === "distributing_partner" || o.fulfilled_by_role === "distributing_partner" || o.source === "wholesale-dp-invoice");
        }).reduce((s, o) => s + (Number(o.total) || 0), 0);
        const lowStock = products.filter(p => (p.warehouse_stock || 0) < 50).length;
        const cutoff = Date.now() - 48 * 3600 * 1000;
        const outstandingPOs = pos.filter(p => p.status === "draft" && new Date(p.created_at).getTime() < cutoff).length;
        const backOrders = orders.filter(o => o.status === "BackOrder").length;
        setStats({ clients: clients.length, orders: orders.length, revenue, pendingPayments, dpRevenue, personnel: personnel.length, lowStock, alerts, outstandingPOs, backOrders });
        // VIPs — top 10 by lifetime_spend
        const vips = [...clients]
          .filter(c => (c.lifetime_spend || 0) > 0)
          .sort((a, b) => (b.lifetime_spend || 0) - (a.lifetime_spend || 0))
          .slice(0, 10);
        setVipClients(vips);
        api.get("/digest/weekly-trends").then(r => setTrends(r.data)).catch(() => {});
        api.get("/reminders").then(r => setReminders(r.data)).catch(() => {});
      } catch { /* ignore */ }
    })();
  }, []);

  return (
    <div className="p-3 sm:p-6 lg:p-10 max-w-full">
      <header className="flex items-end justify-between mb-8">
        <div>
          <p className="label-eyebrow">Overview</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-1">Hello, {user?.name?.split(" ")[0]}.</h1>
        </div>
        <Link to="/clients" className="btn-brand hidden lg:inline-flex" data-testid="header-add-client-link">Open clients</Link>
      </header>

      {/* Client-only: prominent license-required banner. Renders null for HQ/personnel. */}
      <ClientLicenseAlert />

      {/* Admin-only: event submissions awaiting approval. Renders null when empty / non-admin. */}
      {isAdminUser(user) && <EventModeration />}

      {/* DP-only: commission payout preview. Renders null when the user isn't tied to a DP. */}
      <TipEarningsCard />

      {/* DP-only: Go Active toggle for live GPS dispatch. Renders null for non-DP users. */}
      <GoActiveToggle />
      <PartnerPortalCard />

      {/* ────────────────────────────────────────────────────────────
          MOBILE-ONLY dashboard header layout (< lg). Order matches
          user spec: Morning digest → Recent alerts → 3 KPI tiles
          (Clients/Orders/DPs) → 3 KPI tiles (POs/Inventory/Revenue).
          The trends / reminders / VIP row below stays shared.
          ──────────────────────────────────────────────────────────── */}
      <div className="lg:hidden space-y-3 mb-4">
        {!dismissed && (stats.outstandingPOs > 0 || stats.backOrders > 0 || stats.lowStock > 0) && (
          <div className="p-3 rounded-lg border border-amber-300 bg-amber-50 flex items-start justify-between gap-3" data-testid="morning-digest-mobile">
            <div className="text-xs">
              <p className="label-eyebrow text-amber-700">Morning digest</p>
              <p className="mt-1 text-amber-900 leading-snug">
                {stats.outstandingPOs > 0 && <><strong>{stats.outstandingPOs}</strong> draft PO{stats.outstandingPOs === 1 ? "" : "s"} · </>}
                {stats.backOrders > 0 && <><strong>{stats.backOrders}</strong> back-order{stats.backOrders === 1 ? "" : "s"} · </>}
                {stats.lowStock > 0 && <><strong>{stats.lowStock}</strong> low stock</>}
              </p>
            </div>
            <button onClick={dismissDigest} className="text-amber-700 text-xs shrink-0">Dismiss</button>
          </div>
        )}
        <Card className="p-3" data-testid="recent-alerts-mobile">
          <div className="flex items-center gap-2 mb-2"><BellRing size={14} /><span className="label-eyebrow text-[10px]">Recent alerts</span></div>
          <ul className="space-y-1.5 text-xs">
            {stats.alerts.slice(0, 3).map((a) => (
              <li key={a.id} className="border-b border-[var(--border)] pb-1.5">
                <div className="font-semibold leading-tight">{a.title}</div>
                <div className="text-gray-500 text-[11px] leading-tight">{a.message}</div>
              </li>
            ))}
            {stats.alerts.length === 0 && <li className="text-gray-400">No alerts yet.</li>}
          </ul>
        </Card>
        <div className="grid grid-cols-3 gap-2">
          <Link to="/clients"><Kpi icon={Users} label="Clients" value={stats.clients} testid="mkpi-clients" compact /></Link>
          <Link to="/orders"><Kpi icon={ShoppingCart} label="Orders" value={stats.orders} testid="mkpi-orders" compact /></Link>
          <Link to="/partners"><Kpi icon={Truck} label="DPs" value={stats.personnel} testid="mkpi-eps" compact /></Link>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Link to="/purchase-orders"><Kpi icon={ShoppingCart} label="Open POs" value={stats.outstandingPOs} testid="mkpi-pos" compact tone={stats.outstandingPOs > 0 ? "amber" : "emerald"} /></Link>
          <Link to="/inventory"><Kpi icon={Package} label="Low stock" value={stats.lowStock} testid="mkpi-inv" compact tone={stats.lowStock > 0 ? "amber" : "emerald"} /></Link>
          <Link to="/analytics"><Kpi icon={DollarSign} label="Revenue" value={`$${(stats.revenue/1000).toFixed(1)}k`} testid="mkpi-rev" compact /></Link>
        </div>
      </div>

      {/* ────────────────────────────────────────────────────────────
          DESKTOP dashboard header layout (≥ lg). Untouched.
          ──────────────────────────────────────────────────────────── */}
      <div className="hidden lg:block">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-5 grid grid-cols-2 gap-4" data-testid="kpi-clients"><div><div className="text-[11px] uppercase tracking-wide text-gray-500">Clients</div><div className="text-2xl font-bold mt-2">{stats.clients}</div></div><div className="border-l pl-4"><div className="text-[11px] uppercase tracking-wide text-gray-500">New This Week</div><div className="text-2xl font-bold mt-2">{Number(stats.newClientsThisWeek || 0)}</div><div className="text-xs text-gray-500">Sun–Sat</div></div></Card>
        <Card className="p-5 grid grid-cols-3 gap-3" data-testid="kpi-orders"><div><div className="text-[11px] uppercase tracking-wide text-gray-500">Orders</div><div className="text-2xl font-bold mt-2">{stats.orders}</div></div><div className="border-l pl-3"><div className="text-[11px] uppercase tracking-wide text-gray-500">Retail</div><div className="text-2xl font-bold mt-2">{Number(stats.retailOrders || 0)}</div></div><div className="border-l pl-3"><div className="text-[11px] uppercase tracking-wide text-gray-500">Sub-stores</div><div className="text-2xl font-bold mt-2">{Number(stats.substoreOrders || 0)}</div></div></Card>
        <Card className="p-5 grid grid-cols-2 gap-4" data-testid="kpi-revenue"><div><div className="text-[11px] uppercase tracking-wide text-gray-500">Total Revenue</div><div className="text-2xl font-bold mt-2">${stats.revenue.toFixed(2)}</div></div><div className="border-l pl-4"><div className="text-[11px] uppercase tracking-wide text-gray-500">Outstanding</div><div className="text-2xl font-bold mt-2">${Number(stats.pendingPayments || 0).toFixed(2)}</div><div className="text-xs text-gray-500">Pending Payments</div></div></Card>
        <Card className="p-4 sm:p-5 grid grid-cols-[0.8fr_1.5fr_0.85fr] gap-2 sm:gap-3" data-testid="kpi-personnel"><div className="min-w-0"><div className="text-[10px] sm:text-[11px] uppercase tracking-wide text-gray-500">Active DPs</div><div className="text-xl sm:text-2xl font-bold mt-2">{stats.personnel}</div></div><div className="min-w-0 border-l pl-2 sm:pl-3"><div className="text-[10px] sm:text-[11px] uppercase tracking-wide text-gray-500">DP Revenue</div><div className="text-lg sm:text-2xl font-bold mt-2 tabular-nums whitespace-nowrap" data-testid="dp-revenue-value">${Number(stats.dpRevenue || 0).toFixed(2)}</div></div><div className="min-w-0 border-l pl-2 sm:pl-3"><div className="text-[10px] sm:text-[11px] uppercase tracking-wide text-gray-500">WHC Orders</div><div className="text-xl sm:text-2xl font-bold mt-2">{Number(stats.whcOrders || 0)}</div></div></Card>
      </div>

      <div className="mt-4">
        <Link to="/purchase-orders" className="block">
          <Card className={`p-5 border-l-4 ${stats.outstandingPOs > 0 ? "border-amber-500 bg-amber-50" : "border-emerald-500"}`} data-testid="outstanding-pos-widget">
            <div className="flex items-center justify-between">
              <div>
                <p className="label-eyebrow">Outstanding purchase orders</p>
                <p className="text-2xl font-bold mt-1">{stats.outstandingPOs} draft PO{stats.outstandingPOs === 1 ? "" : "s"} older than 48h</p>
                <p className="text-xs text-gray-500 mt-1">{stats.outstandingPOs > 0 ? "Chase the vendor or mark confirmed →" : "All caught up."}</p>
              </div>
              <ShoppingCart size={28} className={stats.outstandingPOs > 0 ? "text-amber-600" : "text-emerald-600"} />
            </div>
          </Card>
        </Link>
      </div>

      {/* Sub-store discount + Default MPire Buck$ split — HQ-only.
          Admins can view read-only; only Super Admin can modify. Personnel
          & clients don't see these at all. */}
      {canSeeHqSettings && (
        <>
          <Card className="p-5 mt-4" data-testid="sub-store-discount-card">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-[var(--brand)]/10 text-[var(--brand)] flex items-center justify-center shrink-0">
                  <Percent size={18} />
                </div>
                <div className="min-w-0">
                  <p className="label-eyebrow flex items-center gap-1.5">
                    Client sub-store discount
                    {!isSuperAdmin && (
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-semibold text-amber-800 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded" title="Only Super Admin can edit">
                        <Lock size={9} /> View only
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500 leading-snug">Applied to every self-enrolled client's sub-store. Their customers see retail prices minus this %.</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Input
                  type="number" min="0" max="100" step="0.5"
                  value={subDiscount}
                  onChange={e => setSubDiscount(e.target.value)}
                  placeholder={discountLoaded ? "" : "—"}
                  disabled={!discountLoaded || !isSuperAdmin}
                  className="w-24 text-right tabular-nums"
                  data-testid="sub-store-discount-input"
                />
                <span className="text-sm text-gray-500">%</span>
                {isSuperAdmin && (
                  <Button className="btn-brand" onClick={saveDiscount} disabled={savingDiscount} data-testid="sub-store-discount-save">
                    {savingDiscount ? "Saving…" : "Save"}
                  </Button>
                )}
              </div>
            </div>
          </Card>

          <Card className="p-5 mt-4" data-testid="default-split-card">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                  <DollarSign size={18} />
                </div>
                <div className="min-w-0">
                  <p className="label-eyebrow flex items-center gap-1.5">
                    Default MPire Buck$ split
                    {!isSuperAdmin && (
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-semibold text-amber-800 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded" title="Only Super Admin can edit">
                        <Lock size={9} /> View only
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500 leading-snug">Applied to every new self-enrolled client. Hidden from clients — HQ can override per-client on their profile.</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Input
                  type="number" min="0" max="100" step="0.5"
                  value={defaultSplit}
                  onChange={e => setDefaultSplit(e.target.value)}
                  placeholder={splitLoaded ? "" : "—"}
                  disabled={!splitLoaded || !isSuperAdmin}
                  className="w-24 text-right tabular-nums"
                  data-testid="default-split-input"
                />
                <span className="text-sm text-gray-500">%</span>
                {isSuperAdmin && (
                  <Button className="btn-brand" onClick={saveDefaultSplit} disabled={savingSplit} data-testid="default-split-save">
                    {savingSplit ? "Saving…" : "Save"}
                  </Button>
                )}
              </div>
            </div>
          </Card>

          <Card className="p-5 mt-4" data-testid="trending-count-card">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                  <Flame size={18} />
                </div>
                <div className="min-w-0">
                  <p className="label-eyebrow flex items-center gap-1.5">
                    Trending This Week · top products shown
                    {!isSuperAdmin && (
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-semibold text-amber-800 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded" title="Only Super Admin can edit">
                        <Lock size={9} /> View only
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500 leading-snug">Ranks the past 7 days of paid orders and shows the top N on the shop &amp; every sub-portal (min 1, max 10). Hidden automatically when there are no recent orders.</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Input
                  type="number" min="1" max="10" step="1"
                  value={trendingCount}
                  onChange={e => setTrendingCount(e.target.value)}
                  placeholder={trendingCountLoaded ? "" : "—"}
                  disabled={!trendingCountLoaded || !isSuperAdmin}
                  className="w-24 text-right tabular-nums"
                  data-testid="trending-count-input"
                />
                <span className="text-sm text-gray-500">tiles</span>
                {isSuperAdmin && (
                  <Button className="btn-brand" onClick={saveTrendingCount} disabled={savingTrendingCount} data-testid="trending-count-save">
                    {savingTrendingCount ? "Saving…" : "Save"}
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </>
      )}

      {/* Trending This Week — informational widget for HQ */}
      <TrendingBanner priceKind="retail" tone="brand" />

      {/* Dynamic Bundles · Package Deals — Super Admin edits; admins view read-only; hidden from personnel. */}
      <BundlesManager />

      {/* Residual Payout · Super Admin only (owner emails auto-promoted).
          COMPLETELY separate from MPire Buck$ discount credits. */}
      {(user?.role === "super_admin" || ["jaytoe.tukanu@outlook.com","jaytoe.tukan@gmail.com"].includes((user?.email || "").toLowerCase())) && (
        <ResidualPayoutSection />
      )}

      {/* Inventory Health row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-3"><Package size={16} /><span className="label-eyebrow">Inventory health</span></div>
          <div className="text-2xl font-semibold">{stats.lowStock} products below threshold</div>
          <p className="text-sm text-gray-500 mt-1">Items with warehouse stock under 50 units.</p>
          <Link to="/inventory" className="inline-block mt-4 text-sm text-[var(--brand)] hover:underline" data-testid="dashboard-inventory-link">
            Manage inventory →
          </Link>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3"><BellRing size={16} /><span className="label-eyebrow">Recent alerts</span></div>
          <ul className="space-y-2 text-sm">
            {stats.alerts.slice(0, 5).map((a) => (
              <li key={a.id} className="border-b border-[var(--border)] pb-2">
                <div className="font-semibold">{a.title}</div>
                <div className="text-gray-500 text-xs">{a.message}</div>
              </li>
            ))}
            {stats.alerts.length === 0 && <li className="text-gray-400 text-sm">No alerts yet.</li>}
          </ul>
        </Card>
      </div>

      {/* Morning digest — moved below Inventory Health */}
      {!dismissed && (stats.outstandingPOs > 0 || stats.backOrders > 0 || stats.lowStock > 0) && (
        <div className="mt-6 p-4 rounded-lg border border-amber-300 bg-amber-50 flex items-start justify-between gap-4" data-testid="morning-digest">
          <div className="text-sm">
            <p className="label-eyebrow text-amber-700">Morning digest · {new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</p>
            <p className="mt-1 text-amber-900">
              {stats.outstandingPOs > 0 && <><strong>{stats.outstandingPOs}</strong> draft PO{stats.outstandingPOs === 1 ? "" : "s"} older than 48h · </>}
              {stats.backOrders > 0 && <><strong>{stats.backOrders}</strong> client order{stats.backOrders === 1 ? "" : "s"} in BackOrder · </>}
              {stats.lowStock > 0 && <><strong>{stats.lowStock}</strong> product{stats.lowStock === 1 ? "" : "s"} below warehouse threshold</>}
            </p>
            <p className="text-xs text-amber-700 mt-1">Sent to HQ each morning via Resend at 07:00 ET.</p>
          </div>
          <button onClick={dismissDigest} className="text-amber-700 hover:text-amber-900 text-xs" data-testid="dismiss-digest">Dismiss</button>
        </div>
      )}
      </div>{/* /desktop-only header layout */}

      {/* Weekly trends + Reminders — moved below Inventory Health */}
      {trends && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6" data-testid="trends-reminders-row">
          <Card className="p-4" data-testid="weekly-trends">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5"><TrendingUp size={13} className="text-[var(--brand)]" /><span className="label-eyebrow">Weekly trends · 7d</span></div>
              <span className="text-[10px] text-gray-400">ET</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[11px] text-gray-500 mb-0.5">Orders/day</div>
                <div style={{ width: "100%", height: 68 }} data-testid="orders-bar-chart">
                  <ResponsiveContainer>
                    <BarChart data={trends.days} margin={{ top: 2, right: 2, left: 0, bottom: 0 }}>
                      <XAxis dataKey="label" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} interval={0} />
                      <Tooltip cursor={{ fill: "#F3F4F6" }} contentStyle={{ fontSize: 11, borderRadius: 6, padding: 4 }} />
                      <Bar dataKey="orders" fill="var(--brand)" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-[11px] text-gray-500 mt-1 mb-0.5">Revenue</div>
                <div style={{ width: "100%", height: 26 }} data-testid="revenue-sparkline">
                  <ResponsiveContainer>
                    <LineChart data={trends.days} margin={{ top: 1, right: 1, left: 1, bottom: 1 }}>
                      <Line type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={1.5} dot={false} />
                      <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6, padding: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div>
                <div className="text-[11px] text-gray-500 mb-1">By DP</div>
                <ul className="space-y-1 max-h-28 overflow-y-auto" data-testid="ep-revenue-list">
                  {trends.by_partner.length === 0 && <li className="text-gray-400 text-[11px]">No orders</li>}
                  {trends.by_partner.map((p) => {
                    const max = Math.max(...trends.by_partner.map((x) => x.revenue), 1);
                    const pct = Math.max(3, (p.revenue / max) * 100);
                    return (
                      <li key={p.name} className="text-[11px]" data-testid={`ep-row-${p.name.replace(/\s+/g, "-")}`}>
                        <div className="flex justify-between">
                          <span className="font-semibold text-gray-700 truncate mr-1">{p.name}</span>
                          <span className="text-gray-500 tabular-nums">${p.revenue.toFixed(0)}</span>
                        </div>
                        <div className="h-0.5 rounded-full bg-gray-100 overflow-hidden">
                          <div className="h-full bg-[var(--brand)]" style={{ width: `${pct}%` }} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </Card>

          <Card className="p-4" data-testid="reminders-widget">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5"><BellRing size={13} className="text-pink-500" /><span className="label-eyebrow">Reminders · next 7d</span></div>
              <span className="text-[10px] text-gray-400">{reminders.length} upcoming</span>
            </div>
            {reminders.length === 0 ? (
              <p className="text-[11px] text-gray-400 py-6 text-center">No birthdays or anniversaries this week.</p>
            ) : (
              <ul className="space-y-1.5 max-h-[130px] overflow-y-auto" data-testid="reminders-list">
                {reminders.slice(0, 8).map((r, i) => (
                  <li key={`${r.client_id}-${r.type}-${i}`} data-testid={`reminder-item-${r.client_id}-${r.type}`}>
                    <Link to={`/clients/${r.client_id}`} className="flex items-center gap-2 hover:bg-gray-50 rounded px-1.5 py-1 group">
                      {r.type === "birthday"
                        ? <Cake size={13} className="text-pink-500 shrink-0" />
                        : <Gift size={13} className="text-purple-500 shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-medium capitalize truncate group-hover:text-[var(--brand)]">{r.client_name}</div>
                        <div className="text-[10px] text-gray-500">
                          {r.type} · {r.days_until === 0 ? "today" : r.days_until === 1 ? "tomorrow" : `in ${r.days_until}d`}
                        </div>
                      </div>
                      <span className="text-[10px] text-gray-400 tabular-nums">{r.date?.slice(5)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {reminders.length > 8 && (
              <div className="mt-1 pt-2 border-t border-[var(--border)] text-right">
                <Link to="/reminders" className="text-[10px] text-[var(--brand)] hover:underline">View all →</Link>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* VIP Clients — moved below Inventory Health */}
      {vipClients.length > 0 && (
        <Card className="p-5 mt-6 border-amber-200" data-testid="vip-clients">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Crown size={16} className="text-amber-500" />
              <span className="label-eyebrow">VIP Clients · top 10 by lifetime spend</span>
            </div>
            <Link to="/clients" className="text-xs text-[var(--brand)] hover:underline" data-testid="vip-view-all">View all clients →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {vipClients.map((c, i) => {
              const totalTop = vipClients[0]?.lifetime_spend || 1;
              const pct = Math.max(6, ((c.lifetime_spend || 0) / totalTop) * 100);
              return (
                <Link key={c.id} to={`/clients/${c.id}`} className="group flex items-center gap-3 p-2 rounded-lg hover:bg-amber-50 transition"
                      data-testid={`vip-client-${c.id}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i < 3 ? "bg-amber-400 text-white" : "bg-amber-100 text-amber-700"}`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold text-gray-800 truncate capitalize group-hover:text-[var(--brand)]">{c.name}</span>
                      <span className="text-xs font-bold text-emerald-600 tabular-nums ml-2">
                        ${(c.lifetime_spend || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                    <div className="h-1 mt-1 rounded-full bg-amber-100 overflow-hidden">
                      <div className="h-full bg-amber-400" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                      {c.transaction_count || 0} txns{c.last_visit ? ` · last visit ${c.last_visit}` : ""}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}
